import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Form, Input } from 'antd'
import {
  enterpriseUpdateBasicsSchema,
  type EnterpriseSummary,
  type EnterpriseUpdateBasicsInput
} from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'

export function EnterpriseBasicsModal({ initial, onClose }: { initial: EnterpriseSummary; onClose: () => void }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [error, setError] = useState<string>()
  const mutation = useMutation(trpc.order.subjects.updateBasics.mutationOptions())
  return (
    <ModalForm<EnterpriseUpdateBasicsInput>
      open
      title="编辑服务对象基本信息"
      layout="vertical"
      initialValues={{ displayName: initial.displayName, note: initial.note }}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      modalProps={{ mask: { closable: false }, closable: !mutation.isPending, keyboard: !mutation.isPending }}
      submitter={{
        submitButtonProps: { loading: mutation.isPending },
        resetButtonProps: { disabled: mutation.isPending }
      }}
      onFinish={async (values) => {
        const parsed = enterpriseUpdateBasicsSchema.safeParse({
          ...values,
          subjectId: initial.id,
          expectedVersion: initial.version
        })
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message)
          return false
        }
        try {
          await mutation.mutateAsync(parsed.data)
          await Promise.all([
            cache.invalidateQueries(trpc.order.subjects.list.queryFilter()),
            cache.invalidateQueries(trpc.order.subjects.options.queryFilter()),
            cache.invalidateQueries(trpc.order.subjects.detail.queryFilter({ subjectId: initial.id }))
          ])
          void message.success('服务对象基本信息已更新')
          onClose()
          return true
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : '保存失败，未提交改动已保留')
          return false
        }
      }}
    >
      {error && <Alert className="mb-4" type="error" title={error} />}
      <Form.Item label="所属客户">
        <Input value={initial.customerName} disabled />
      </Form.Item>
      <Form.Item
        label="内部称呼"
        name="displayName"
        rules={[{ required: true, whitespace: true, message: '请输入内部称呼' }]}
      >
        <Input maxLength={255} disabled={mutation.isPending} />
      </Form.Item>
      <Form.Item label="备注" name="note">
        <Input.TextArea rows={3} maxLength={5000} disabled={mutation.isPending} />
      </Form.Item>
    </ModalForm>
  )
}
